/**
 * The cross-app attribution contract: the two query params a funnel link
 * carries and the shape a lead id must have to travel in one.
 *
 * Four apps are on this wire, deployed independently:
 *
 *  - apps/go mints the visitor id and 307s with `?visitor-id=…&go-path=…`
 *    (worker/lib/redirect.ts, `buildDestination`);
 *  - apps/www adopts the id, persists it, and stamps it on every outbound
 *    link (src/lib/attribution.ts);
 *  - apps/retells adopts it from that link (src/lib/attribution.ts) and
 *    hands it to its worker (worker/visitor.ts) and Convex
 *    (packages/convex-retells/convex/model/attribution.ts);
 *  - apps/study's funnel reads both params off a scanned bookmark
 *    (worker/lib/funnel.ts).
 *
 * A rename on one side does not break any build: it silently ends
 * attribution, because the other side reads a param nobody sends. Before
 * this module each app spelled the strings itself and the only test pinned
 * one app's constant to a literal. Every app imports them from here now, so
 * the one remaining way to break the contract is to change a value below,
 * which ./attribution.test.ts refuses. These strings are also in links
 * already printed, emailed and bookmarked: they are the same forever.
 *
 * Plain constants and a pure function, no runtime dependency, so a Worker, a
 * browser bundle and a Convex function can all import it.
 */

/** The query param the lead (visitor) id travels in. */
export const LEAD_PARAM = "visitor-id";

/** The query param naming the go/ short link a visitor arrived through. */
export const GO_PATH_PARAM = "go-path";

/**
 * The shape a lead id must have to be adopted, stored or forwarded.
 *
 * Opaque ids only: an id one app accepts and the next rejects travels one
 * hop and is dropped, which is worse than never having had one, because the
 * link looks tagged. The same shape is the `SAFE_ID` the log ingest checks
 * (packages/logging/src/ingest.ts), so an id that reaches a table is one the
 * logs kept too.
 */
export const LEAD_ID_PATTERN = /^[a-zA-Z0-9_-]{1,100}$/;

/** True when `value` is shaped like a lead id any app here would accept. */
export function isValidLeadId(value: unknown): value is string {
  return typeof value === "string" && LEAD_ID_PATTERN.test(value);
}

/**
 * The shape an ad platform's click id must have to be kept. Looser than the
 * lead id because the platforms pick the alphabet: `.` is allowed (Meta's
 * `fbclid` is dotted) and the cap is 256 (a `gclid` runs 100–200).
 *
 * Over-long ids are rejected, never truncated: a sliced id passes every check
 * here and is then uploaded as a garbage id that matches no click.
 */
export const CLICK_ID_PATTERN = /^[a-zA-Z0-9_.-]{1,256}$/;

/**
 * The click-id params recognised, most preferred first; the order is the
 * tie-break when a landing URL carries several, since Google's import takes
 * exactly one per event. Google arrives under three names: on iOS with ATT
 * denied there is no `gclid`, only `gbraid` (app-to-web) or `wbraid`
 * (web-to-app), and recognising only `gclid` loses the whole iOS ad group.
 */
export const KNOWN_CLICK_SOURCES = [
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
  "ttclid",
  "rdt_cid",
  "epik",
  "li_fat_id",
] as const;

export type ClickSource = (typeof KNOWN_CLICK_SOURCES)[number];
